import React from 'react';
import { BRAND } from '../../lib/brand';
import { MODES } from './onboarding';
import { badge, btnSmall, compactInput, hint, warnBox } from './ui';

// One row per website feeding the corpus: what the probe found, and how the
// crawler should read it (mode, sitemap or listed pages, PDFs linked from pages,
// whose site it is, excludes).
// Controlled: rows in, onChange(rows) out.

function ProbeSummary({ probe }) {
  if (!probe) return null;
  const facts = [];
  if (probe.platform && probe.platform !== 'unknown') facts.push(probe.platform);
  if (probe.wp_rest === 'open' && probe.wp_counts) {
    const c = probe.wp_counts;
    facts.push(`WP API: ${c.pages ?? '?'} pages, ${c.posts ?? '?'} posts, ${c.pdfs ?? '?'} PDFs`);
  } else if (probe.wp_rest === 'locked') {
    facts.push('WP API locked');
  }
  if (probe.sitemap) facts.push(`sitemap: ${probe.sitemap.urls} URLs${probe.sitemap.pdfs ? `, ${probe.sitemap.pdfs} PDFs` : ''}`);
  return (
    <div style={{ fontSize: '0.8rem', color: BRAND.gray600, marginTop: '4px' }}>
      {(probe.title || probe.site_name) && (
        <div>
          <span style={{ color: BRAND.gray500 }}>Site says it is: </span>
          <b>{probe.site_name || probe.title}</b>
          {probe.site_name && probe.title && probe.site_name !== probe.title && <span> · “{probe.title}”</span>}
        </div>
      )}
      {facts.length > 0 && <div>{facts.join(' · ')}</div>}
      {probe.reason && <div style={{ color: BRAND.gray500 }}>{probe.reason}</div>}
      {(probe.warnings || []).map((w) => <div key={w} style={warnBox}>{w}</div>)}
    </div>
  );
}

export function ExcludeEditor({ rows, onChange, idPrefix, showHelp, title = 'Keep out of the corpus' }) {
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: BRAND.ink900 }}>{title}</div>
      {rows.map((r, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '4px', flexWrap: 'wrap' }}>
          <select aria-label={`${idPrefix} exclude ${i + 1} type`} value={r.kind}
                  onChange={(e) => set(i, { kind: e.target.value })} style={{ ...compactInput, width: 'auto' }}>
            <option value="glob">Path</option>
            <option value="rule">URL regex</option>
          </select>
          <input aria-label={`${idPrefix} exclude ${i + 1} pattern`} value={r.pattern}
                 placeholder={r.kind === 'glob' ? '/Member-Central/*' : '/wp-content/uploads/.+/Rt_\\d+\\.pdf$'}
                 onChange={(e) => set(i, { pattern: e.target.value })}
                 style={{ ...compactInput, flex: '1 1 220px', fontFamily: 'monospace' }} />
          {r.kind === 'rule' && (
            <input aria-label={`${idPrefix} exclude ${i + 1} uploaded before`} type="date" value={r.before}
                   title="Only documents uploaded before this day (WordPress dates)"
                   onChange={(e) => set(i, { before: e.target.value })} style={{ ...compactInput, width: 'auto' }} />
          )}
          <button type="button" style={btnSmall} aria-label={`${idPrefix} remove exclude ${i + 1}`}
                  onClick={() => onChange(rows.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <button type="button" style={{ ...btnSmall, marginTop: '6px' }}
              onClick={() => onChange([...rows, { kind: 'glob', pattern: '', before: '' }])}>
        + Exclude
      </button>
      {showHelp && <p style={hint}>
        Path: a glob from the start of the path (<code>/Member-Central/*</code>, <code>/calendar.php?*</code>).
        URL regex: matched against the whole URL; add a date to drop only documents uploaded before it (superseded schedules).
      </p>}
    </div>
  );
}

export default function SourcesEditor({ rows, onChange, channel, onChannel, allowSiteEdit = false }) {
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div>
      {rows.map((r, i) => {
        const id = `source-${i + 1}`;
        return (
          <div key={r.key || i} style={{
            padding: '12px 14px', border: `1px solid ${BRAND.rule}`, borderRadius: '8px', marginBottom: '10px',
            background: r.include ? '#fff' : '#f8fafc', opacity: r.include ? 1 : 0.75,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input type="checkbox" id={`${id}-include`} checked={r.include}
                     onChange={(e) => set(i, { include: e.target.checked })} />
              {allowSiteEdit ? (
                <input aria-label={`${id} site`} value={r.site} placeholder="https://www.example.org"
                       onChange={(e) => set(i, { site: e.target.value.trim().replace(/\/+$/, ''), key: r.key || e.target.value })}
                       style={{ ...compactInput, flex: '1 1 260px' }} />
              ) : (
                <label htmlFor={`${id}-include`} style={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.site || r.probe?.input}</label>
              )}
              {onChannel && r.include && r.site && (
                channel === r.site
                  ? <span style={badge('#e0e7ff', '#3730a3')}>main site</span>
                  : <button type="button" style={btnSmall} onClick={() => onChannel(r.site)}>Make main site</button>
              )}
              <span style={{ flex: 1 }} />
              <label htmlFor={`${id}-mode`} style={{ fontSize: '0.78rem', color: BRAND.gray500 }}>Crawl by</label>
              <select id={`${id}-mode`} aria-label={`${id} mode`} value={r.mode} disabled={!r.include}
                      onChange={(e) => set(i, { mode: e.target.value })} style={{ ...compactInput, width: 'auto' }}>
                {MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
              <button type="button" style={btnSmall} aria-label={`${id} remove`}
                      onClick={() => onChange(rows.filter((_, j) => j !== i))}>Remove</button>
            </div>
            <ProbeSummary probe={r.probe} />
            {r.include && (
              <>
                <p style={hint}>{MODES.find((m) => m.id === r.mode)?.help}</p>
                {r.mode === 'pages' && (
                  <div style={{ marginTop: '6px' }}>
                    <textarea aria-label={`${id} pages`} rows={6} value={r.pagesText || ''}
                              placeholder={`${r.site || 'https://www.county.gov'}/162/Elections`}
                              onChange={(e) => set(i, { pagesText: e.target.value })}
                              style={{ ...compactInput, width: '100%', fontFamily: 'monospace' }} />
                    <label style={{ fontSize: '0.8rem', display: 'inline-flex', gap: '5px', alignItems: 'center' }}>
                      <input type="checkbox" aria-label={`${id} linked PDFs`} checked={!!r.linkedPdfs}
                             onChange={(e) => set(i, { linkedPdfs: e.target.checked })} />
                      Also PDFs linked from those pages
                    </label>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
                  <input aria-label={`${id} publisher`} value={r.publisher || ''}
                         placeholder="Publisher, for another organization's site (e.g. Iredell County)"
                         title="Put in front of every page title from this site, so answers say who handles it"
                         onChange={(e) => set(i, { publisher: e.target.value })}
                         style={{ ...compactInput, flex: '1 1 300px' }} />
                </div>
                {r.mode !== 'crawl' && r.mode !== 'pages' && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '6px', flexWrap: 'wrap' }}>
                    <input aria-label={`${id} sitemap URL`} value={r.sitemapUrl}
                           placeholder={r.mode === 'wp' ? 'Sitemap for gap-fill (optional; default /sitemap.xml)' : 'https://…/sitemap.xml'}
                           onChange={(e) => set(i, { sitemapUrl: e.target.value.trim() })}
                           style={{ ...compactInput, flex: '1 1 300px' }} />
                    {r.mode === 'sitemap' && (
                      <label style={{ fontSize: '0.8rem', display: 'inline-flex', gap: '5px', alignItems: 'center' }}>
                        <input type="checkbox" aria-label={`${id} linked PDFs`} checked={r.linkedPdfs}
                               onChange={(e) => set(i, { linkedPdfs: e.target.checked })} />
                        Also PDFs linked from those pages
                      </label>
                    )}
                  </div>
                )}
                <ExcludeEditor idPrefix={id} rows={r.excludes || []} showHelp={i === rows.findIndex((x) => x.include)}
                               onChange={(excludes) => set(i, { excludes })} />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
